import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { Url } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { QueueRule } from '@rossum/api-client/schemas';
import { notify } from '../../toaster';
import { RULES_QUERY_KEY } from './rulesQueryKeys';
import { usePatchRule } from './usePatchRule';

type BulkRemoveQueuesParams = {
  rule: QueueRule;
  queues: Queue[];
  ruleQueues: Url[];
};

export const useBulkRemoveQueues = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { mutateAsync: patchRule } = usePatchRule();

  return useMutation({
    mutationFn: async ({
      rule,
      queues,
      ruleQueues,
    }: BulkRemoveQueuesParams) => {
      if (queues.length === 0) {
        return Promise.reject(new Error('No queues to remove'));
      }

      const queueUrls = queues.map(q => q.url);
      const updatedQueues = ruleQueues.filter(q => !queueUrls.includes(q));

      if (updatedQueues.length === 0) {
        return Promise.reject(
          new Error('Cannot remove all queues from a rule')
        );
      }

      await patchRule({
        patchedRuleId: rule.id,
        patchedRule: { queues: updatedQueues },
        skipInvalidation: true,
      });

      return { removedQueues: queues, updatedQueues };
    },
    onSuccess: (_, { rule, ruleQueues }) => {
      queryClient.invalidateQueries([RULES_QUERY_KEY]);

      const toastId = `undo-bulk-remove-queues-${rule.id}`;
      notify.default({
        title: intl.formatMessage({
          id: 'features.ruleManager.queues.remove.success',
        }),
        toastActions: [
          {
            label: intl.formatMessage({
              id: 'components.toaster.actions.undo',
            }),
            onClick: () => {
              toast.dismiss(toastId);
              patchRule({
                patchedRuleId: rule.id,
                patchedRule: { queues: ruleQueues },
              }).catch(() =>
                notify.error({
                  title: intl.formatMessage({
                    id: 'features.queueSettings.rules.list.remove.undo.notification.failure',
                  }),
                })
              );
            },
          },
        ],
        id: toastId,
      });
    },
    onError: () =>
      notify.error({
        title: intl.formatMessage({
          id: 'features.ruleManager.queues.remove.error',
        }),
      }),
  });
};
